import { formatDistanceToNowStrict, set } from "date-fns";
import enUSLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import jaLocale from "date-fns/locale/ja";
import { useContext, useEffect, useRef, useState } from "react";
import { csrfToken } from "../../../core/network/csrf_token";
import { ProjectsPageLocaleContext } from "./ProjectsPage";
import { axiosWithRails } from "./axiosWithRails";
import { ProgrammingLanguage } from "./language";
import "../../../packs/stylesheets/projects.css.scss";

type propsType = {
  loginUser: {
    id: string | null;
  };
  project: {
    uid: string;
    id: string;
    title: string | null;
    language: string;
    createdAt: Date;
    share: "private" | "public";
    isFavorite: boolean;
    isFeatured: boolean;
    sourceCode: string;
    user: {
      id: string;
      name: string;
      imageUrl: string | null;
    } | null;
  };
};

export const ReadOnlySourceCode = (props: propsType) => {
  const localeKey = useContext(ProjectsPageLocaleContext);
  const locales = {
    "ja-jp": jaLocale, // 日本語
    "en-us": enUSLocale, // 英語(アメリカ)
    "es-es": esLocale, // スペイン語
  };
  const locale = locales[localeKey] || jaLocale;

  const editorContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const editor = (window as any).ace.edit(editorContainerRef.current!);
    editor.setTheme("ace/theme/textmate");
    editor.session.setMode(`ace/mode/${ProgrammingLanguage.getAceMode(props.project.language)}`);
    editor.session.setValue(props.project.sourceCode);
    editor.setOptions({ maxLines: Number.POSITIVE_INFINITY });
    editor.setReadOnly(true);
  }, []);

  const [isPrivate, setIsPrivate] = useState(props.project.share === "private");
  const [isFavorite, setIsFavorite] = useState(props.project.isFavorite);

  // aタグでのプロジェクトへのリンク先を定義する
  const projectPath = `/projects/${props.project.uid}`;

  // axiosのheadersの共通設定
  axiosWithRails.defaults.headers.common["Content-Type"] = "application/json";
  axiosWithRails.defaults.headers.common["X-CSRF-Token"] = String(csrfToken());

  /* 公開非公開の切り替え */
  const updateShareToPublic = async () => {
    setIsPrivate(false);

    // APIを呼び出してプロジェクトの共有設定の更新を試みる
    axiosWithRails
      .patch(`/api/projects/${props.project.uid}.json`, {
        share: "public",
      })
      .then((response) => {
        // 200でエラーが返ってきた場合も例外を投げる
        if (response.data.error) {
          throw new Error(response.data.error);
        }
      })
      .catch((err) => {
        // 更新に失敗した場合はログを出してアイコンを非公開に戻す
        console.error("Failed to update item:", err);
        setIsPrivate(true);
      });
  };

  const updateShareToPrivate = async () => {
    setIsPrivate(true);

    // APIを呼び出してプロジェクトの共有設定の更新を試みる
    axiosWithRails
      .patch(`/api/projects/${props.project.uid}.json`, {
        share: "private",
      })
      .then((response) => {
        // 200でエラーが返ってきた場合も例外を投げる
        if (response.data.error) {
          throw new Error(response.data.error);
        }
      })
      .catch((err) => {
        // 更新に失敗した場合はログを出してアイコンを公開に戻す
        console.error("Failed to update item:", err);
        setIsPrivate(false);
      });
  };

  /* お気に入りの切り替え */
  const createFavorite = async () => {
    setIsFavorite(true);

    // APIを呼び出してお気に入り登録を試みる
    axiosWithRails
      .post("/api/favorites.json", {
        project_id: props.project.id,
      })
      .then((response) => {
        // 200でエラーが返ってきた場合も例外を投げる
        if (response.data?.error) {
          throw new Error(response.data.error);
        }
      })
      .catch((err) => {
        // 登録に失敗した場合はログを出してアイコンを枠のみの星に戻す
        console.error("Failed to create item:", err);
        setIsFavorite(false);
      });
  };

  const deleteFavorite = async () => {
    setIsFavorite(false);

    // APIを呼び出してお気に入り削除を試みる
    axiosWithRails
      .delete(`/api/favorites.json?project_id=${props.project.id}`)
      .then((response) => {
        // 200でエラーが返ってきた場合も例外を投げる
        if (response.data?.error) {
          throw new Error(response.data.error);
        }
      })
      .catch((err) => {
        // 削除に失敗した場合はログを出してアイコンを色付きの星に戻す
        console.error("Failed to delete item:", err);
        setIsFavorite(true);
      });
  };

  return (
    <>
      <div className="project-panel">
        <div className="panel panel-default">
          <div className="panel-heading">
            <table style={{ border: "none", width: "100%" }}>
              <tbody>
                <tr>
                  <td align="left" className="project-title-bar-left">
                    <a
                      href={projectPath}
                      style={{
                        color: "inherit",
                        textDecorationColor: "#2692AC",
                      }}
                    >
                      {" "}
                      {/* 下線の色はapplication.cssのa.no-colorの指定と合わせる */}
                      <b className="mr-4">{props.project.title}</b>
                      <b className="mr-4">
                        {`(${ProgrammingLanguage.getLabel(props.project.language)})`}
                      </b>
                    </a>
                    {props.project.user !== null &&
                      (props.project.user.imageUrl ? (
                        <img
                          className="mr-4"
                          src={props.project.user.imageUrl}
                          style={{ width: "16px", height: "16px" }}
                        />
                      ) : (
                        <i
                          className="glyphicon glyphicon-user default-user-color mr-4"
                          title={props.project.user.name}
                        /> /* todo: ツールチップを付けたらtitleは消す */
                      ))}
                    {props.loginUser.id &&
                      props.project.user &&
                      props.loginUser.id === props.project.user.id && (
                        <i
                          className={`glyphicon ${isPrivate ? "glyphicon-lock" : "glyphicon-globe"} mr-4`}
                          onClick={
                            isPrivate
                              ? updateShareToPublic
                              : updateShareToPrivate
                          }
                        />
                      )}
                    {props.loginUser.id && (
                      <i
                        className={`glyphicon ${isFavorite ? "glyphicon-star" : "glyphicon-star-empty"} mr-4 `}
                        onClick={isFavorite ? deleteFavorite : createFavorite}
                      />
                    )}
                    {props.project.isFeatured && (
                      <i className="glyphicon glyphicon-ok mr-4" />
                    )}
                  </td>
                  <td align="right">
                    {formatDistanceToNowStrict(props.project.createdAt, {
                      addSuffix: true,
                      locale: locale,
                    })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <a href={projectPath}>
            <div className="panel-body">
              <div
                style={{ width: "100%", height: "100%" }}
                ref={editorContainerRef}
              />
            </div>
          </a>
        </div>
      </div>
    </>
  );
};
